import { useMemo, useState } from "react";
import { I_Building, I_Pin, I_Plus, I_Search } from "./icons";
import { Pill } from "./ui";
import { BUDGETS, STATUSES_P, TYPES, sStyle } from "../constants.js";
import { fmtCents, fmtINR, priceInRange } from "../lib/format.js";
import { glass, glassHov, heading, input, monoFont } from "../styles.js";

const PropertiesView = ({ properties, onOpen, onNew }) => {
    const [q, setQ] = useState(""),
      [type, setType] = useState(""),
      [st, setSt] = useState(""),
      [bud, setBud] = useState(""),
      [palc, setPalc] = useState("");
    const filtered = useMemo(() => {
      let l = properties;
      if (q) {
        const x = q.toLowerCase();
        l = l.filter((p) =>
          [p.title, p.locality, p.panchayat, p.code, p.description, p.zone].some((f) =>
            (f || "").toLowerCase().includes(x),
          ),
        );
      }
      if (type) l = l.filter((p) => p.type === type);
      if (st === "Allotted") l = l.filter((p) => !!p.allotted);
      else if (st) l = l.filter((p) => p.status === st);
      if (bud) l = l.filter((p) => priceInRange(bud, p.price_lakhs));
      if (palc === "yes") l = l.filter((p) => !!p.allotted);
      else if (palc === "no") l = l.filter((p) => !p.allotted);
      return l;
    }, [properties, q, type, st, bud, palc]);
    const Chip = ({ on, onClick, children, dot }) => (
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition ${on ? "bg-teal-300 text-slate-900" : "bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:bg-white/[0.08]"}`}
      >
        {dot && !on && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
        {children}
      </button>
    );
    return (
      <div className="space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl text-slate-100 mb-1" style={heading}>
              Properties
            </h1>
            <p className="text-sm text-slate-400">
              {filtered.length} of {properties.length} shown
            </p>
          </div>
          <button
            onClick={onNew}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-300 text-slate-900 hover:bg-teal-200 transition"
          >
            <I_Plus size={14} />
            New property
          </button>
        </div>
        <div className={`${glass} rounded-xl p-3 space-y-3`}>
          <div className="relative">
            <I_Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, locality, code…"
              className={`${input} pl-9`}
            />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Status</span>
            <Chip on={!st} onClick={() => setSt("")}>
              All
            </Chip>
            {STATUSES_P.map((s) => (
              <Chip
                key={s}
                on={st === s}
                onClick={() => setSt(st === s ? "" : s)}
                dot={sStyle[s]?.dot}
              >
                {s}
              </Chip>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              Type
            </span>
            <Chip on={!type} onClick={() => setType("")}>
              All
            </Chip>
            {TYPES.map((t) => (
              <Chip key={t} on={type === t} onClick={() => setType(type === t ? "" : t)}>
                {t}
              </Chip>
            ))}
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              Allotted
            </span>
            <Chip on={!palc} onClick={() => setPalc("")}>
              Any
            </Chip>
            <Chip
              on={palc === "yes"}
              onClick={() => setPalc(palc === "yes" ? "" : "yes")}
              dot="bg-sky-400"
            >
              Yes
            </Chip>
            <Chip
              on={palc === "no"}
              onClick={() => setPalc(palc === "no" ? "" : "no")}
              dot="bg-stone-400"
            >
              No
            </Chip>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-3 mr-1">
              In budget
            </span>
            <Chip on={!bud} onClick={() => setBud("")}>
              Any
            </Chip>
            {BUDGETS.slice(0, 7).map((b) => (
              <Chip key={b} on={bud === b} onClick={() => setBud(bud === b ? "" : b)}>
                {b}
              </Chip>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className={`${glass} rounded-2xl p-12 text-center`}>
            <I_Building size={32} className="mx-auto mb-2 text-slate-600" />
            <div className="text-sm text-slate-400 mb-3">
              {properties.length === 0 ? "No properties yet." : "Nothing matches your filters."}
            </div>
            {properties.length === 0 && (
              <button
                onClick={onNew}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-teal-300 text-slate-900"
              >
                <I_Plus size={14} />
                Add your first property
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((p) => (
              <button
                key={p.id}
                onClick={() => onOpen(p.id)}
                className={`${glass} ${glassHov} rounded-xl overflow-hidden text-left transition`}
              >
                <div className="aspect-[16/10] bg-white/[0.04] relative overflow-hidden">
                  {p.cover_photo ? (
                    <img src={p.cover_photo} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-600">
                      <I_Building size={32} />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    {p.code && (
                      <span
                        className="text-[10px] font-medium text-slate-100 bg-slate-950/60 backdrop-blur px-2 py-0.5 rounded"
                        style={monoFont}
                      >
                        {p.code}
                      </span>
                    )}
                    {p.status && (
                      <Pill
                        label={p.status}
                        s={sStyle[p.status] || sStyle.Available}
                        small={true}
                      />
                    )}
                    {p.allotted && <Pill label="Allotted" s={sStyle.Allotted} small={true} />}
                  </div>
                </div>
                <div className="p-3.5">
                  <h3 className="text-base text-slate-100 leading-tight" style={heading}>
                    {p.title}
                  </h3>
                  <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1 truncate">
                    <I_Pin size={10} />
                    {p.locality || "Location not set"}
                    {p.panchayat ? ", " + p.panchayat : ""}
                  </div>
                  <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-white/[0.06]">
                    <div className="text-xs text-slate-300">
                      {p.type && <span>{p.type}</span>}
                      {p.land_cents && (
                        <span className="text-slate-500"> · {fmtCents(p.land_cents)}</span>
                      )}
                    </div>
                    <div className="text-sm text-amber-200 font-medium" style={monoFont}>
                      {fmtINR(p.price_lakhs)}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

export { PropertiesView };
